import { useEffect, useMemo, useState } from 'react'

import { useSetAtom } from 'jotai'
import type { components } from 'src/lib/api-client/schema.gen'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'

import { templateDetailAtom } from './atom'
import {
  useSubmitTemplate,
  useSubmitTemplateOrder,
} from './use-submit-template'
import { validateMediumItems } from './validate'
import { MediumItem } from '../../../../../../entities/medium-item/entity'
import { Template } from '../../../../../../entities/template/entity'

export const useEditTemplate = ({
  template,
  mediumItems,
  observationType,
}: {
  template: Template | undefined
  mediumItems: MediumItem[]
  observationType?: components['schemas']['enums.ObservationType']
}) => {
  const [releaseTried, setReleaseTried] = useState(false)

  const setAtom = useSetAtom(templateDetailAtom)
  const submitTemplate = useSubmitTemplate()
  const submitTemplateOrder = useSubmitTemplateOrder()

  const v2 = true

  useEffect(() => {
    if (!template) return

    setAtom({ type: 'initWithTemplate', template })
    setReleaseTried(false)
  }, [setAtom, template])

  const toast = useMirohaToast()

  const handlers = useMemo(
    () => ({
      onValidate: () => {
        setReleaseTried(true)
        return validateMediumItems(mediumItems, v2, observationType)
      },
      onAdd: () => setAtom({ type: 'addMediumItem' }),
      onSubmit: async () => {
        setReleaseTried(false)
        try {
          await submitTemplate()
          toast({
            status: 'success',
            title: '一時保存しました。',
          })
        } catch (error) {
          toast({
            status: 'error',
            title: error.message,
          })
        }
      },
      onSubmitOrder: async () => {
        if (!v2) return
        try {
          await submitTemplateOrder()
          toast({
            status: 'success',
            title: '順番を保存しました。',
          })
        } catch (error) {
          toast({
            status: 'error',
            title: error.message,
          })
        }
      },
      onChangeTitle: (title: string) => setAtom({ type: 'updateTitle', title }),
      onChangeVideoEnabled: (videoEnabled: boolean) =>
        setAtom({ type: 'updateVideoEnabled', videoEnabled }),
    }),
    [
      setAtom,
      submitTemplate,
      submitTemplateOrder,
      mediumItems,
      v2,
      toast,
      observationType,
    ],
  )

  return {
    releaseTried,
    handlers,
  }
}
